/**
 * Enhanced Email Templates for Kitium Auth
 * Production-ready HTML and text templates with professional styling
 */

import { EmailTemplate } from './types'

export interface TemplateVariables {
  code?: string
  link?: string
  appUrl?: string
  appName?: string
  userName?: string
  expiresIn?: string
  supportEmail?: string
}

/**
 * Base template styles and HTML structure
 */
const baseStyles = `
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', sans-serif;
  color: #333;
  line-height: 1.6;
`

/**
 * Password Reset Email Template
 */
export function createPasswordResetTemplate(
  resetLink: string,
  variables: TemplateVariables = {}
): EmailTemplate {
  const appName = variables.appName || 'Kitium Auth'
  const expiresIn = variables.expiresIn || '1 hour'
  const supportEmail = variables.supportEmail || 'support@example.com'

  return {
    subject: `Reset your ${appName} password`,
    html: `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body { margin: 0; padding: 0; }
          table { border-collapse: collapse; }
          .container { max-width: 600px; margin: 0 auto; }
          .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 40px 20px; text-align: center; }
          .header h1 { margin: 0; font-size: 28px; }
          .content { background: #f9fafb; padding: 40px 20px; }
          .button { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold; }
          .footer { background: #f3f4f6; padding: 20px; font-size: 12px; color: #6b7280; text-align: center; border-top: 1px solid #e5e7eb; }
          .warning { background: #fef3c7; border-left: 4px solid #f59e0b; padding: 16px; border-radius: 4px; margin: 20px 0; }
          .code { background: #f3f4f6; padding: 12px; border-radius: 4px; font-family: monospace; word-break: break-all; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>Password Reset</h1>
          </div>
          <div class="content">
            <p>Hi ${variables.userName || 'User'},</p>
            <p>We received a request to reset the password for your ${appName} account.</p>
            <div style="text-align: center; margin: 30px 0;">
              <a href="${resetLink}" class="button">Reset Password</a>
            </div>
            <div class="warning">
              <strong>Security Note:</strong> This link expires in ${expiresIn}. If you didn't request this, please ignore this email.
            </div>
            <p>Or copy and paste this link in your browser:</p>
            <div class="code">${resetLink}</div>
            <p style="margin-top: 30px;">
              If you have any questions, please contact us at <a href="mailto:${supportEmail}">${supportEmail}</a>.
            </p>
            <p style="margin-top: 30px;">
              Best regards,<br>
              <strong>The ${appName} Team</strong>
            </p>
          </div>
          <div class="footer">
            <p>This is an automated message. Please don't reply to this email.</p>
            <p>&copy; ${new Date().getFullYear()} ${appName}. All rights reserved.</p>
          </div>
        </div>
      </body>
      </html>
    `,
    text: `Reset Your ${appName} Password

Hi ${variables.userName || 'User'},

We received a request to reset the password for your ${appName} account.

Click the link below to reset your password:
${resetLink}

This link expires in ${expiresIn}. If you didn't request this, please ignore this email.

If you have any questions, please contact us at ${supportEmail}.

Best regards,
The ${appName} Team

---
This is an automated message. Please don't reply to this email.`
  }
}

/**
 * Email Verification Template
 */
export function createEmailVerificationTemplate(
  verificationLink: string,
  variables: TemplateVariables = {}
): EmailTemplate {
  const appName = variables.appName || 'Kitium Auth'
  const expiresIn = variables.expiresIn || '1 hour'
  const supportEmail = variables.supportEmail || 'support@example.com'

  return {
    subject: `Verify your ${appName} email address`,
    html: `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body { margin: 0; padding: 0; }
          table { border-collapse: collapse; }
          .container { max-width: 600px; margin: 0 auto; }
          .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 40px 20px; text-align: center; }
          .header h1 { margin: 0; font-size: 28px; }
          .content { background: #f9fafb; padding: 40px 20px; }
          .button { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold; }
          .footer { background: #f3f4f6; padding: 20px; font-size: 12px; color: #6b7280; text-align: center; border-top: 1px solid #e5e7eb; }
          .info-box { background: #dbeafe; border-left: 4px solid #3b82f6; padding: 16px; border-radius: 4px; margin: 20px 0; }
          .code { background: #f3f4f6; padding: 12px; border-radius: 4px; font-family: monospace; word-break: break-all; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>Verify Your Email</h1>
          </div>
          <div class="content">
            <p>Hi ${variables.userName || 'User'},</p>
            <p>Thank you for signing up for ${appName}! Please verify your email address to get started.</p>
            <div style="text-align: center; margin: 30px 0;">
              <a href="${verificationLink}" class="button">Verify Email</a>
            </div>
            <div class="info-box">
              <strong>Note:</strong> This link expires in ${expiresIn}.
            </div>
            <p>Or copy and paste this link in your browser:</p>
            <div class="code">${verificationLink}</div>
            <p style="margin-top: 30px;">
              Once verified, you'll have full access to ${appName}.
            </p>
            <p>
              If you have any questions, please contact us at <a href="mailto:${supportEmail}">${supportEmail}</a>.
            </p>
            <p style="margin-top: 30px;">
              Best regards,<br>
              <strong>The ${appName} Team</strong>
            </p>
          </div>
          <div class="footer">
            <p>This is an automated message. Please don't reply to this email.</p>
            <p>&copy; ${new Date().getFullYear()} ${appName}. All rights reserved.</p>
          </div>
        </div>
      </body>
      </html>
    `,
    text: `Verify Your ${appName} Email Address

Hi ${variables.userName || 'User'},

Thank you for signing up for ${appName}! Please verify your email address to get started.

Click the link below to verify your email:
${verificationLink}

This link expires in ${expiresIn}.

Once verified, you'll have full access to ${appName}.

If you have any questions, please contact us at ${supportEmail}.

Best regards,
The ${appName} Team

---
This is an automated message. Please don't reply to this email.`
  }
}

/**
 * Verification Code Template (for 6-digit or similar codes)
 */
export function createVerificationCodeTemplate(
  code: string,
  variables: TemplateVariables = {}
): EmailTemplate {
  const appName = variables.appName || 'Kitium Auth'
  const expiresIn = variables.expiresIn || '10 minutes'

  return {
    subject: `Your ${appName} verification code is ${code}`,
    html: `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body { margin: 0; padding: 0; }
          .container { max-width: 600px; margin: 0 auto; }
          .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 40px 20px; text-align: center; }
          .header h1 { margin: 0; font-size: 28px; }
          .content { background: #f9fafb; padding: 40px 20px; }
          .code-box { background: white; border: 2px solid #667eea; border-radius: 8px; padding: 30px; text-align: center; margin: 30px 0; }
          .code { font-size: 48px; font-weight: bold; color: #667eea; letter-spacing: 8px; font-family: monospace; }
          .footer { background: #f3f4f6; padding: 20px; font-size: 12px; color: #6b7280; text-align: center; border-top: 1px solid #e5e7eb; }
          .warning { background: #fef3c7; border-left: 4px solid #f59e0b; padding: 16px; border-radius: 4px; margin: 20px 0; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>Verification Code</h1>
          </div>
          <div class="content">
            <p>Hi ${variables.userName || 'User'},</p>
            <p>Your verification code is:</p>
            <div class="code-box">
              <div class="code">${code}</div>
            </div>
            <div class="warning">
              <strong>Don't share this code.</strong> We will never ask for it. It expires in ${expiresIn}.
            </div>
            <p>
              If you didn't request this code, you can safely ignore this email.
            </p>
          </div>
          <div class="footer">
            <p>This is an automated message. Please don't reply to this email.</p>
            <p>&copy; ${new Date().getFullYear()} ${appName}. All rights reserved.</p>
          </div>
        </div>
      </body>
      </html>
    `,
    text: `Your ${appName} Verification Code

Hi ${variables.userName || 'User'},

Your verification code is:

${code}

Don't share this code. We will never ask for it. It expires in ${expiresIn}.

If you didn't request this code, you can safely ignore this email.

---
This is an automated message. Please don't reply to this email.`
  }
}

/**
 * Login Link Template
 */
export function createLoginLinkTemplate(
  loginLink: string,
  variables: TemplateVariables = {}
): EmailTemplate {
  const appName = variables.appName || 'Kitium Auth'
  const expiresIn = variables.expiresIn || '1 hour'
  const supportEmail = variables.supportEmail || 'support@example.com'

  return {
    subject: `Your ${appName} login link`,
    html: `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body { margin: 0; padding: 0; }
          .container { max-width: 600px; margin: 0 auto; }
          .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 40px 20px; text-align: center; }
          .header h1 { margin: 0; font-size: 28px; }
          .content { background: #f9fafb; padding: 40px 20px; }
          .button { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold; }
          .footer { background: #f3f4f6; padding: 20px; font-size: 12px; color: #6b7280; text-align: center; border-top: 1px solid #e5e7eb; }
          .security { background: #dbeafe; border-left: 4px solid #3b82f6; padding: 16px; border-radius: 4px; margin: 20px 0; }
          .code { background: #f3f4f6; padding: 12px; border-radius: 4px; font-family: monospace; word-break: break-all; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>Your Login Link</h1>
          </div>
          <div class="content">
            <p>Hi ${variables.userName || 'User'},</p>
            <p>Click the link below to securely log in to your ${appName} account.</p>
            <div style="text-align: center; margin: 30px 0;">
              <a href="${loginLink}" class="button">Log In</a>
            </div>
            <div class="security">
              <strong>Security:</strong> This link expires in ${expiresIn} and can only be used once.
            </div>
            <p>Or copy and paste this link in your browser:</p>
            <div class="code">${loginLink}</div>
            <p style="margin-top: 30px;">
              If you didn't request this login link, you can safely ignore this email.
            </p>
            <p>
              Questions? Contact us at <a href="mailto:${supportEmail}">${supportEmail}</a>.
            </p>
            <p style="margin-top: 30px;">
              Best regards,<br>
              <strong>The ${appName} Team</strong>
            </p>
          </div>
          <div class="footer">
            <p>This is an automated message. Please don't reply to this email.</p>
            <p>&copy; ${new Date().getFullYear()} ${appName}. All rights reserved.</p>
          </div>
        </div>
      </body>
      </html>
    `,
    text: `Your ${appName} Login Link

Hi ${variables.userName || 'User'},

Click the link below to securely log in to your ${appName} account.

${loginLink}

This link expires in ${expiresIn} and can only be used once.

If you didn't request this login link, you can safely ignore this email.

Questions? Contact us at ${supportEmail}.

Best regards,
The ${appName} Team

---
This is an automated message. Please don't reply to this email.`
  }
}

/**
 * Welcome Email Template
 */
export function createWelcomeTemplate(variables: TemplateVariables = {}): EmailTemplate {
  const appName = variables.appName || 'Kitium Auth'
  const appUrl = variables.appUrl || 'https://example.com'
  const supportEmail = variables.supportEmail || 'support@example.com'

  return {
    subject: `Welcome to ${appName}!`,
    html: `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body { margin: 0; padding: 0; }
          .container { max-width: 600px; margin: 0 auto; }
          .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 40px 20px; text-align: center; }
          .header h1 { margin: 0; font-size: 28px; }
          .content { background: #f9fafb; padding: 40px 20px; }
          .button { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 12px 30px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold; }
          .footer { background: #f3f4f6; padding: 20px; font-size: 12px; color: #6b7280; text-align: center; border-top: 1px solid #e5e7eb; }
          .feature-list { background: white; border-left: 4px solid #667eea; padding: 20px; margin: 20px 0; border-radius: 4px; }
          .feature-list ul { margin: 0; padding-left: 20px; }
          .feature-list li { margin: 8px 0; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>Welcome to ${appName}!</h1>
          </div>
          <div class="content">
            <p>Hi ${variables.userName || 'User'},</p>
            <p>Welcome to ${appName}! Your account has been successfully created.</p>
            <div style="text-align: center; margin: 30px 0;">
              <a href="${appUrl}" class="button">Get Started</a>
            </div>
            <div class="feature-list">
              <p><strong>What's Next?</strong></p>
              <ul>
                <li>Complete your profile</li>
                <li>Explore our features</li>
                <li>Customize your preferences</li>
                <li>Connect with the community</li>
              </ul>
            </div>
            <p>
              If you have any questions or need help getting started, please don't hesitate to contact us at <a href="mailto:${supportEmail}">${supportEmail}</a>.
            </p>
            <p>
              We're excited to have you on board!
            </p>
            <p style="margin-top: 30px;">
              Best regards,<br>
              <strong>The ${appName} Team</strong>
            </p>
          </div>
          <div class="footer">
            <p>This is an automated message. Please don't reply to this email.</p>
            <p>&copy; ${new Date().getFullYear()} ${appName}. All rights reserved.</p>
          </div>
        </div>
      </body>
      </html>
    `,
    text: `Welcome to ${appName}!

Hi ${variables.userName || 'User'},

Welcome to ${appName}! Your account has been successfully created.

Get started at: ${appUrl}

What's Next?
- Complete your profile
- Explore our features
- Customize your preferences
- Connect with the community

If you have any questions or need help getting started, please don't hesitate to contact us at ${supportEmail}.

We're excited to have you on board!

Best regards,
The ${appName} Team

---
This is an automated message. Please don't reply to this email.`
  }
}
